import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Send, Mail, MessageSquare, MessageCircle, ChevronRight } from "lucide-react";
import { staffApi } from "@/lib/staffApi";
import { Skeleton, ErrorState } from "@/components/BookingShell";
import { Pill, CHANNEL_LABEL, TRIGGER_LABEL, formatDateTime } from "./shared";

const CHANNEL_ICON = { email: Mail, sms: MessageSquare, whatsapp: MessageCircle };

// Which queue states can transition into which
const TRANSITIONS = {
    draft:      ["pending", "scheduled", "cancelled"],
    pending:    ["scheduled", "processing", "cancelled", "skipped"],
    scheduled:  ["processing", "cancelled", "skipped"],
    processing: ["sent", "failed", "cancelled"],
    sent:       [],
    failed:     ["scheduled", "cancelled"],
    cancelled:  [],
    skipped:    [],
};

export function CommsQueue() {
    const [items, setItems] = useState(null);
    const [stats, setStats] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [meta, setMeta] = useState(null);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState("all");
    const [channelFilter, setChannelFilter] = useState("all");
    const [busy, setBusy] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [expanded, setExpanded] = useState(null);

    const load = () => {
        setError(null);
        const p = { limit: 200 };
        if (statusFilter !== "all") p.status = statusFilter;
        if (channelFilter !== "all") p.channel = channelFilter;
        Promise.all([
            staffApi.commsListQueue(p),
            staffApi.commsListTemplates({ status: "published" }),
            staffApi.commsMeta(),
        ]).then(([q, t, m]) => {
            setItems(q.items || []);
            setStats(q.stats || {});
            setTemplates(t.templates || []);
            setMeta(m);
        }).catch(setError);
    };
    useEffect(load, [statusFilter, channelFilter]);

    const doStatus = async (item, status) => {
        setBusy(true);
        try {
            await staffApi.commsQueueStatus(item.id, status);
            toast.success(`Marked as ${status}`);
            load();
        } catch (e) { toast.error(e.message || "Update failed"); }
        finally { setBusy(false); }
    };

    const doDelete = async (id) => {
        if (!window.confirm("Delete this queue item?")) return;
        setBusy(true);
        try { await staffApi.commsDeleteQueueItem(id); toast.success("Deleted"); load(); }
        catch (e) { toast.error(e.message || "Delete failed"); }
        finally { setBusy(false); }
    };

    if (error) return <ErrorState error={error} onRetry={load} />;

    return (
        <div className="space-y-4" data-testid="comms-queue-page">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                    <h2 className="font-serif text-2xl text-white">Send queue</h2>
                    <p className="text-sm text-white/50 mt-1">Preview & manage queued communications. Automations dispatch via SMTP — status transitions are real.</p>
                </div>
                <button onClick={() => setShowNew(true)} data-testid="new-queue-btn"
                        className="h-9 px-4 rounded-full bg-gold text-navy font-semibold text-xs inline-flex items-center gap-1">
                    <Plus className="h-3.5 w-3.5" /> Simulate entry
                </button>
            </div>

            <div className="flex flex-wrap gap-2">
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="queue-status-filter"
                        className="h-9 rounded-full bg-transparent border hairline text-xs text-white/80 px-3">
                    <option value="all">All statuses</option>
                    {(meta?.queue_statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={channelFilter} onChange={(e) => setChannelFilter(e.target.value)} data-testid="queue-channel-filter"
                        className="h-9 rounded-full bg-transparent border hairline text-xs text-white/80 px-3">
                    <option value="all">All channels</option>
                    {(meta?.channels || []).map((c) => <option key={c} value={c}>{CHANNEL_LABEL[c]}</option>)}
                </select>
            </div>

            {!items ? <Skeleton className="h-40" /> : items.length === 0 ? (
                <div className="rounded-2xl border hairline p-8 text-center" data-testid="queue-empty">
                    <p className="text-white/60 text-sm">No queue items match these filters.</p>
                </div>
            ) : (
                <ul className="space-y-2" data-testid="queue-list">
                    {items.map((it) => {
                        const Icon = CHANNEL_ICON[it.channel] || Mail;
                        const isOpen = expanded === it.id;
                        return (
                            <li key={it.id} className="rounded-2xl border hairline" data-testid={`queue-row-${it.id}`}>
                                <button onClick={() => setExpanded(isOpen ? null : it.id)} className="w-full text-left p-4 flex items-center gap-3">
                                    <div className="h-10 w-10 rounded-full glass flex items-center justify-center shrink-0">
                                        <Icon className="h-4 w-4 text-gold" strokeWidth={1.5} />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-white font-ui text-sm truncate">{it.template_name || "—"}</div>
                                        <div className="text-[10px] uppercase tracking-[0.16em] text-white/45 flex flex-wrap gap-2 mt-0.5">
                                            <span>{TRIGGER_LABEL[it.trigger] || it.trigger}</span>
                                            <span>·</span>
                                            <span>{formatDateTime(it.scheduled_at)}</span>
                                            {it.recipient?.email && <><span>·</span><span className="normal-case tracking-normal text-white/60">{it.recipient.email}</span></>}
                                            {it.recipient?.phone && <><span>·</span><span className="normal-case tracking-normal text-white/60">{it.recipient.phone}</span></>}
                                        </div>
                                    </div>
                                    <Pill value={it.status} />
                                    <ChevronRight className={`h-4 w-4 text-white/40 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                                </button>
                                {isOpen && (
                                    <div className="border-t hairline p-4 space-y-3" data-testid={`queue-expand-${it.id}`}>
                                        {/* Rendered content */}
                                        {it.rendered?.subject && (
                                            <div>
                                                <div className="text-[10px] uppercase tracking-[0.16em] text-white/45">Subject</div>
                                                <div className="text-white text-sm">{it.rendered.subject}</div>
                                            </div>
                                        )}
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.16em] text-white/45">Body</div>
                                            <div className="text-white text-sm whitespace-pre-wrap bg-white/[0.02] rounded-lg border hairline p-3 max-h-56 overflow-y-auto">
                                                {it.rendered?.body || <span className="text-white/40">—</span>}
                                            </div>
                                        </div>
                                        {/* Transitions */}
                                        <div className="flex flex-wrap gap-2 items-center">
                                            <div className="text-[10px] uppercase tracking-[0.16em] text-white/45">Move to:</div>
                                            {(TRANSITIONS[it.status] || []).map((s) => (
                                                <button key={s} onClick={() => doStatus(it, s)} disabled={busy} data-testid={`queue-transition-${it.id}-${s}`}
                                                        className="h-8 px-3 rounded-full border hairline text-[11px] text-white/80 hover:text-gold inline-flex items-center gap-1">
                                                    <Send className="h-3 w-3" /> {s}
                                                </button>
                                            ))}
                                            {(TRANSITIONS[it.status] || []).length === 0 && (
                                                <span className="text-[11px] text-white/40">Terminal state</span>
                                            )}
                                            <button onClick={() => doDelete(it.id)} disabled={busy} data-testid={`queue-delete-${it.id}`}
                                                    className="h-8 px-3 rounded-full border hairline text-[11px] text-red-300 hover:border-red-300/50 inline-flex items-center gap-1 ml-auto">
                                                <Trash2 className="h-3 w-3" /> Delete
                                            </button>
                                        </div>
                                        {/* History */}
                                        {it.history && it.history.length > 0 && (
                                            <div>
                                                <div className="text-[10px] uppercase tracking-[0.16em] text-white/45 mb-1">Lifecycle history</div>
                                                <ul className="text-[11px] text-white/70 space-y-0.5">
                                                    {it.history.map((h, i) => (
                                                        <li key={i} className="flex gap-2">
                                                            <span className="text-white/40">{formatDateTime(h.at)}</span>
                                                            <span>→</span>
                                                            <span className="uppercase tracking-[0.14em]">{h.status}</span>
                                                            {h.note && <span className="text-white/50">· {h.note}</span>}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}

            {showNew && meta && (
                <NewQueueModal
                    meta={meta}
                    templates={templates}
                    onClose={() => setShowNew(false)}
                    onCreated={() => { setShowNew(false); load(); }}
                />
            )}
        </div>
    );
}

function NewQueueModal({ meta, templates, onClose, onCreated }) {
    const [form, setForm] = useState({
        template_id: templates[0]?.id || "",
        trigger: "manual",
        recipient_name: "",
        recipient_email: "",
        recipient_phone: "",
        scheduled_at: new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16),
        status: "scheduled",
        variables: { customer_first_name: "Sarah", service_name: "Consultation", appointment_date: "12 March 2026", appointment_time: "10:45" },
    });
    const [busy, setBusy] = useState(false);
    const template = useMemo(() => templates.find((t) => t.id === form.template_id), [templates, form.template_id]);

    if (!templates.length) {
        return (
            <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4" onClick={onClose} data-testid="queue-empty-modal">
                <div className="w-full max-w-md rounded-3xl glass-strong p-6" onClick={(e) => e.stopPropagation()}>
                    <h3 className="font-serif text-2xl text-white">No published templates</h3>
                    <p className="mt-2 text-sm text-white/60">Publish at least one template before adding queue entries.</p>
                    <div className="mt-4 flex justify-end">
                        <button onClick={onClose} className="h-10 rounded-full border hairline text-white/70 px-4 text-sm">Close</button>
                    </div>
                </div>
            </div>
        );
    }

    const setVar = (k, v) => setForm((f) => ({ ...f, variables: { ...f.variables, [k]: v } }));

    const save = async () => {
        if (!template) return;
        setBusy(true);
        try {
            const iso = new Date(form.scheduled_at).toISOString();
            await staffApi.commsCreateQueueItem({
                template_id: form.template_id,
                channel: template.channel,
                trigger: form.trigger,
                status: form.status,
                scheduled_at: iso,
                recipient: {
                    name: form.recipient_name.trim() || undefined,
                    email: form.recipient_email.trim() || undefined,
                    phone: form.recipient_phone.trim() || undefined,
                },
                variables: form.variables,
            });
            toast.success("Queue entry created");
            onCreated();
        } catch (e) { toast.error(e.message || "Create failed"); }
        finally { setBusy(false); }
    };

    const templateVars = template ? (template.variables || []) : [];

    return (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4 overflow-y-auto py-6" onClick={onClose} data-testid="new-queue-modal">
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg rounded-3xl glass-strong p-6 space-y-3 max-h-[90vh] overflow-y-auto">
                <h3 className="font-serif text-2xl text-white">Simulate a queue entry</h3>
                <p className="text-xs text-white/50">This creates a queue record with rendered content, but does not send anything.</p>

                <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mb-1.5">Template</div>
                    <select value={form.template_id} onChange={(e) => setForm({ ...form, template_id: e.target.value })} data-testid="queue-template"
                            className="w-full bg-transparent border-b hairline py-2 text-white text-sm outline-none">
                        {templates.map((t) => <option key={t.id} value={t.id} className="bg-navy">{t.name} ({CHANNEL_LABEL[t.channel]})</option>)}
                    </select>
                </label>

                <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                        <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mb-1.5">Trigger</div>
                        <select value={form.trigger} onChange={(e) => setForm({ ...form, trigger: e.target.value })} data-testid="queue-trigger"
                                className="w-full bg-transparent border-b hairline py-2 text-white text-sm outline-none">
                            {meta.triggers.map((t) => <option key={t.id} value={t.id} className="bg-navy">{TRIGGER_LABEL[t.id] || t.id}</option>)}
                        </select>
                    </label>
                    <label className="block">
                        <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mb-1.5">Initial status</div>
                        <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="queue-init-status"
                                className="w-full bg-transparent border-b hairline py-2 text-white text-sm outline-none">
                            {["draft","pending","scheduled"].map((s) => <option key={s} value={s} className="bg-navy">{s}</option>)}
                        </select>
                    </label>
                </div>

                <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mb-1.5">Scheduled for</div>
                    <input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
                           data-testid="queue-scheduled"
                           className="bg-transparent border hairline rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-gold" />
                </label>

                <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                        <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mb-1.5">Recipient name</div>
                        <input value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })}
                               data-testid="queue-recipient-name"
                               className="w-full bg-transparent border-b hairline py-2 text-white text-sm outline-none focus:border-gold" />
                    </label>
                    <label className="block">
                        <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mb-1.5">{template?.channel === "email" ? "Email" : "Phone"}</div>
                        {template?.channel === "email" ? (
                            <input type="email" value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })}
                                   data-testid="queue-recipient-email"
                                   className="w-full bg-transparent border-b hairline py-2 text-white text-sm outline-none focus:border-gold" />
                        ) : (
                            <input type="tel" value={form.recipient_phone} onChange={(e) => setForm({ ...form, recipient_phone: e.target.value })}
                                   data-testid="queue-recipient-phone"
                                   className="w-full bg-transparent border-b hairline py-2 text-white text-sm outline-none focus:border-gold" />
                        )}
                    </label>
                </div>

                {templateVars.length > 0 && (
                    <div className="space-y-2">
                        <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">Variables</div>
                        {templateVars.map((v) => (
                            <label key={v} className="block">
                                <div className="text-[10px] text-white/40 mb-1 font-mono">{`{{${v}}}`}</div>
                                <input value={form.variables[v] || ""} onChange={(e) => setVar(v, e.target.value)}
                                       data-testid={`queue-var-${v}`}
                                       className="w-full bg-transparent border-b hairline py-1.5 text-white text-sm outline-none focus:border-gold" />
                            </label>
                        ))}
                    </div>
                )}

                <div className="flex gap-2 justify-end pt-3">
                    <button onClick={onClose} className="h-10 rounded-full border hairline text-white/70 px-4 text-sm">Cancel</button>
                    <button onClick={save} disabled={busy || !template} data-testid="queue-save"
                            className="h-10 rounded-full bg-gold text-navy px-5 text-sm font-semibold disabled:opacity-40">
                        Add to queue
                    </button>
                </div>
            </div>
        </div>
    );
}
